import { Link } from "@tanstack/react-router";
import { Search, ArrowRight } from "lucide-react";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";
import { useState } from "react";
import type { BlogCategory, SidebarWidget } from "@/lib/blog";

type Props = {
  categories: BlogCategory[];
  totalCount: number;
  widgets: SidebarWidget[];
  initialSearch?: string;
  onSearch?: (q: string) => void;
  activeCategory?: string;
  onCategoryClick?: (slug: string) => void;
};

export function BlogSidebar({
  categories,
  totalCount,
  widgets,
  initialSearch = "",
  onSearch,
  activeCategory,
  onCategoryClick,
}: Props) {
  const [q, setQ] = useState(initialSearch);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(q);
      return;
    }
    // Fallback: link back to /blog?s=
    const params = new URLSearchParams();
    if (q) params.set("s", q);
    window.location.href = `/blog${params.toString() ? `?${params}` : ""}`;
  };

  return (
    <aside className="flex flex-col gap-6">
      {/* Sharelo video */}
      <ShareloEmbed id="4XbnluOZ4qbUOqP2ig9X5" title="What a great website actually does" desktopLightbox />

      {/* Free mockup promo */}
      <MockupPromoCard />

      {/* Search */}
      <Card>
        <form onSubmit={handleSubmit} className="relative">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles..."
            className="w-full rounded-full border border-black/10 bg-white px-4 py-2.5 pr-10 text-sm text-gray-950 placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
          />
          <button
            type="submit"
            aria-label="Search"
            className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
          >
            <Search className="h-4 w-4" />
          </button>
        </form>
      </Card>

      {/* Categories */}
      <Card>
        <h3 className="mb-4 font-display text-lg font-bold text-gray-950">Categories</h3>
        <ul className="divide-y divide-black/5">
          {categories.map((c) => (
            <li key={c.slug}>
              <CategoryLink
                slug={c.slug}
                name={c.name}
                count={c.count}
                active={activeCategory === c.slug}
                onClick={onCategoryClick}
              />
            </li>
          ))}
          <li>
            <CategoryLink
              slug="all"
              name="All Articles"
              count={totalCount}
              active={activeCategory === "all"}
              onClick={onCategoryClick}
            />
          </li>
        </ul>
        <div className="mt-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:opacity-80"
          >
            View all categories <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </Card>

      {/* WordPress-driven widgets */}
      {widgets.map((w) => (
        <Card key={w.id}>
          <div
            className="widget-html text-sm text-gray-700"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: w.rendered }}
          />
        </Card>
      ))}
    </aside>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-black/5 sm:p-6">
      {children}
    </div>
  );
}

function CategoryLink({
  slug,
  name,
  count,
  active,
  onClick,
}: {
  slug: string;
  name: string;
  count: number;
  active?: boolean;
  onClick?: (slug: string) => void;
}) {
  const label = (
    <span className="flex items-center justify-between py-2.5 text-sm">
      <span className={`font-medium ${active ? "text-brand-blue" : "text-gray-950"}`}>{name}</span>
      <span className="text-xs font-semibold text-gray-500">{count}</span>
    </span>
  );
  if (onClick) {
    return (
      <button type="button" onClick={() => onClick(slug)} className="block w-full text-left">
        {label}
      </button>
    );
  }
  return (
    <Link to="/blog" className="block">
      {label}
    </Link>
  );
}

function MockupPromoCard() {
  return (
    <Link
      to="/get-mockup"
      className="group relative block overflow-hidden rounded-3xl bg-gradient-to-br from-brand-blue via-purple-500 to-pink-500 p-6 text-white shadow-sm"
    >
      <h3 className="font-display text-2xl font-bold leading-tight">
        Get a free website mockup worth $1,500
      </h3>
      <p className="mt-3 text-sm text-white/85">
        See how we can transform your website and drive real results.
      </p>
      <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-brand-blue transition group-hover:-translate-y-0.5">
        Get My Free Mockup <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}
